import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import {
    Box,
    Card,
    Button,
    Typography,
    Alert,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    CircularProgress,
    LinearProgress,
    Divider,
    TextField,
    MenuItem,
} from "@mui/material";
import { ArrowBack, Download, UploadFile, CheckCircle } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import shipmentService from "../../services/shipmentService";
import documentService from "../../services/documentService";
import { parseCsvToObjects } from "../../utils/csv";
import { selectCurrentUser, selectActiveTin } from "../../redux/slices/authSlice";

const STEPS = { UPLOAD: "upload", REVIEW: "review", INVOICES: "invoices", IMPORTING: "importing", RESULT: "result" };

// A group has no stable server-side id until it's created — this key
// (same shipmentRef the CSV grouped it by, or its first CSV row number as a
// fallback for single-row groups) is what ties an invoice upload picked in
// the INVOICES step back to the shipment that group becomes after Create.
const groupKey = (g) => g.shipmentRef || `row-${g.rowNumbers[0]}`;

// Purchase Invoice upload — same dropzone used by the manual Bulk Add page
// (CustomerBulkAddShipments.jsx), duplicated here since useDropzone is a
// hook and this needs one instance per group rendered in a list.
const GroupInvoiceUpload = ({ files, onChange }) => {
    const onDrop = useCallback(
        (accepted) => {
            onChange([...(files || []), ...accepted.map((f) => Object.assign(f, { preview: URL.createObjectURL(f) }))]);
        },
        [files, onChange],
    );
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "application/pdf": [".pdf"],
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "image/webp": [".webp"],
        },
        maxSize: 10 * 1024 * 1024,
    });
    const removeFile = (idx) => onChange((files || []).filter((_, i) => i !== idx));

    return (
        <Box>
            <Box
                {...getRootProps()}
                sx={{
                    border: "1.5px dashed",
                    borderColor: isDragActive ? "primary.main" : "grey.400",
                    borderRadius: 1.5,
                    py: 1.5,
                    px: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: 0.25,
                    cursor: "pointer",
                    bgcolor: isDragActive ? "rgba(13,98,155,0.04)" : "action.hover",
                }}
            >
                <input {...getInputProps()} />
                <UploadFile sx={{ fontSize: 20, color: "text.disabled", mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                    {files?.length > 0 ? "Attach another file" : "Attach Purchase Invoice"}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                    Drag &amp; drop or click to browse
                </Typography>
            </Box>

            {files?.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.5 }}>
                    {files.map((file, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                p: 1,
                                border: "1.5px solid",
                                borderColor: "success.main",
                                borderRadius: 1,
                                bgcolor: "success.light",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                                <CheckCircle sx={{ fontSize: 18, color: "success.main", flexShrink: 0 }} />
                                <Typography noWrap sx={{ mr: 2, flex: 1 }} variant="body2">
                                    {file.name}
                                </Typography>
                            </Box>
                            <Button size="small" color="error" onClick={() => removeFile(idx)}>
                                Remove
                            </Button>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const csvEscape = (val) => {
    const s = String(val ?? "");
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Builds the import report entirely in the browser from data already in
// memory (the streamed `done` payload) and triggers a download via a
// Blob object URL that's revoked right after — nothing is ever sent to or
// stored on the server, it exists only for the moment of the download.
const downloadReportCsv = (result) => {
    const headers = ["status", "rowNumbers", "shipmentTrackingNumber", "itemCount", "reason"];
    const rows = [
        ...result.created.map((c) => ["created", c.rowNumbers.join("; "), c.trackingNumber, c.itemCount, ""]),
        ...result.failed.map((f) => ["failed", f.rowNumbers.join("; "), "", "", f.errors.join("; ")]),
    ];

    const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bulk-import-report-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Converted from BulkImportShipmentsDialog (a modal) into its own page, same
// as Bulk Add (Manual Entry) already is, so there's enough room to review a
// large CSV without being boxed into a dialog.
const CustomerBulkImportShipments = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef(null);
    const currentUser = useSelector(selectCurrentUser);
    const activeTin = useSelector(selectActiveTin);

    // A multi-TIN customer always operates under one selected TIN — every
    // shipment from this import files under it, same as manual creation.
    const showTinPicker = !!currentUser?.canManageMultipleTins && currentUser?.tins?.length > 0;
    const [tin, setTin] = useState("");

    const [step, setStep] = useState(STEPS.UPLOAD);
    const [fileName, setFileName] = useState("");
    const [parsing, setParsing] = useState(false);
    const [groups, setGroups] = useState([]); // [{shipmentRef, rowNumbers, vendorName, ..., items:[{rowNumber,description,quantity,unitValue,trackingNumber,errors}], errors}]
    const [totalRows, setTotalRows] = useState(0);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState({
        shipmentsDone: 0,
        totalShipments: 0,
        itemsDone: 0,
        totalItems: 0,
    });
    const [result, setResult] = useState(null);
    // { [groupKey]: { files: File[], docType: "invoice" | "other" } } — one
    // entry per group, filled in on the INVOICES step, read back out by
    // groupKey when uploading each file to its newly-created shipment.
    const [invoiceData, setInvoiceData] = useState({});
    const [uploadingInvoices, setUploadingInvoices] = useState(false);

    useEffect(() => {
        if (showTinPicker) setTin(activeTin || currentUser.tins[0].tin);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTin, currentUser]);

    const handleDownloadSample = async () => {
        try {
            await shipmentService.downloadBulkImportSample();
        } catch {
            enqueueSnackbar("Failed to download sample CSV", { variant: "error" });
        }
    };

    const handleFileSelected = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setParsing(true);
        try {
            const text = await file.text();
            const parsed = parseCsvToObjects(text);
            if (!parsed.length) {
                enqueueSnackbar("That CSV has no data rows", { variant: "error" });
                setParsing(false);
                return;
            }

            // Always allow several different orders to share one tracking
            // number — a carrier (most often Amazon) consolidating multiple
            // orders into one box is common enough that this shouldn't need
            // an opt-in checkbox; a tracking number already used on a PAST
            // shipment is still always flagged (that check never depends on
            // this flag).
            const { data } = await shipmentService.bulkImportPreview(parsed, true);
            setGroups(data.data.groups);
            setTotalRows(data.data.totalRows);
            setStep(STEPS.REVIEW);
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to read that CSV file", { variant: "error" });
        } finally {
            setParsing(false);
        }
    };

    const validGroups = groups.filter((g) => g.errors.length === 0);
    const invalidGroups = groups.filter((g) => g.errors.length > 0);
    const totalItems = groups.reduce((n, g) => n + g.items.length, 0);

    const updateInvoiceData = (key, patch) =>
        setInvoiceData((prev) => ({ ...prev, [key]: { files: [], docType: "invoice", ...prev[key], ...patch } }));

    // Every valid shipment needs its own invoice attached before Create is
    // enabled — this is the "select all the invoices, then create" flow,
    // CSV import's only chance to attach one since the CSV format itself has
    // no file column.
    const allInvoicesAttached = validGroups.every((g) => invoiceData[groupKey(g)]?.files?.length > 0);

    const handleConfirm = async () => {
        if (!validGroups.length) return;
        setImporting(true);
        setImportProgress({ shipmentsDone: 0, totalShipments: validGroups.length, itemsDone: 0, totalItems: 0 });
        setStep(STEPS.IMPORTING);
        try {
            const final = await shipmentService.bulkImportConfirm(
                validGroups,
                (evt) => {
                    setImportProgress({
                        shipmentsDone: evt.shipmentsDone ?? 0,
                        totalShipments: evt.totalShipments ?? 0,
                        itemsDone: evt.itemsDone ?? 0,
                        totalItems: evt.totalItems ?? 0,
                    });
                },
                showTinPicker ? tin : undefined,
                true,
            );
            setResult(final);
            setStep(STEPS.RESULT);

            // Best-effort: attach each group's invoice file(s) to its
            // newly-created shipment. A created shipment's `rowNumbers[0]`
            // traces back to the same group via groupKey — validGroups was
            // built from the same `groups` array `final.created` references.
            const uploadsToRun = (final.created || [])
                .map((c) => {
                    const group = validGroups.find((g) => g.rowNumbers[0] === c.rowNumbers[0]);
                    const data = group ? invoiceData[groupKey(group)] : null;
                    return data?.files?.length
                        ? { shipmentId: c.shipmentId, files: data.files, docType: data.docType || "invoice" }
                        : null;
                })
                .filter(Boolean);

            if (uploadsToRun.length) {
                setUploadingInvoices(true);
                await Promise.all(
                    uploadsToRun.map(({ shipmentId, files, docType }) =>
                        documentService.upload(shipmentId, files, docType).catch(() => null),
                    ),
                );
                setUploadingInvoices(false);
            }
        } catch (err) {
            enqueueSnackbar(err.message || "Failed to import shipments", { variant: "error" });
            setStep(STEPS.REVIEW);
        } finally {
            setImporting(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/dashboard/shipments"))}
                    variant="outlined"
                    size="small"
                    disabled={importing}
                >
                    Back
                </Button>
                <Box>
                    <Typography variant="h4" fontWeight={700} color="text.primary">
                        Bulk Import Shipments (CSV)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Upload a filled-in CSV to create several shipments at once
                    </Typography>
                </Box>
            </Box>

            <Card sx={{ p: { xs: 1.5, sm: 3 } }}>
                {step === STEPS.UPLOAD && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            Each CSV row is one item. <strong>vendorName</strong>, <strong>orderNumber</strong>,{" "}
                            <strong>carrier</strong>, and <strong>invoiceTotal</strong> (per shipment) and{" "}
                            <strong>itemDescription</strong> (per item) are required — <strong>trackingNumber</strong>{" "}
                            is optional. Leave <strong>shipmentRef</strong> blank for a simple one-item shipment —
                            give two or more rows the <strong>same shipmentRef</strong> to group them into a single
                            shipment with multiple items (fill vendor/order/carrier/invoiceTotal on just one of those
                            rows; invoiceTotal is the total for the whole shipment). Download the sample file below
                            to see this in action.
                        </Alert>
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            Rows missing a required field are rejected and will not create a shipment. Duplicate
                            order numbers are flagged automatically — both within the file
                            itself and against shipments you've already created. A tracking number already used on
                            a shipment you've created before is also flagged; several different orders in this file
                            sharing the same tracking number (e.g. a carrier consolidating multiple orders into one
                            box) is allowed.
                        </Alert>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 2,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "action.hover",
                            }}
                        >
                            <Box>
                                <Typography variant="body2" fontWeight={600}>
                                    Sample CSV
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Includes a multi-item shipment example (2 rows sharing a shipmentRef)
                                </Typography>
                            </Box>
                            <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadSample}>
                                Download Sample
                            </Button>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1.5,
                                p: 4,
                                borderRadius: 2,
                                border: "2px dashed",
                                borderColor: "divider",
                            }}
                        >
                            {parsing ? (
                                <CircularProgress size={28} />
                            ) : (
                                <UploadFile sx={{ fontSize: 36, color: "text.disabled" }} />
                            )}
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                {fileName || "Choose your filled-in CSV file to upload"}
                            </Typography>
                            <Button variant="contained" component="label" disabled={parsing}>
                                {parsing ? "Reading…" : "Choose File"}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv,text/csv"
                                    hidden
                                    onChange={handleFileSelected}
                                />
                            </Button>
                        </Box>
                    </Box>
                )}

                {step === STEPS.REVIEW && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {showTinPicker && (
                            <Alert severity="info" sx={{ borderRadius: 2, flexShrink: 0 }}>
                                Every shipment from this import will be filed under your currently selected TIN
                                {" — "}
                                <strong>
                                    {currentUser.tins.find((t) => t.tin === tin)?.label
                                        ? `${tin} (${currentUser.tins.find((t) => t.tin === tin).label})`
                                        : tin}
                                </strong>
                                . Switch TINs from the sidebar before importing if this isn't the right one.
                            </Alert>
                        )}
                        <Alert severity={invalidGroups.length ? "warning" : "success"} sx={{ borderRadius: 2, flexShrink: 0 }}>
                            {validGroups.length} of {groups.length} shipment(s) ready to import
                            ({totalItems} item(s) across {totalRows} row(s))
                            {invalidGroups.length ? ` — ${invalidGroups.length} shipment(s) need fixing (shown in red)` : ""}.
                        </Alert>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {groups.map((g, gi) => (
                                <Box
                                    key={gi}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: g.errors.length ? "error.light" : "divider",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1,
                                            px: 1.5,
                                            py: 1,
                                            bgcolor: g.errors.length ? "rgba(197,48,48,0.06)" : "action.hover",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                            <Typography variant="body2" fontWeight={700}>
                                                {g.vendorName || "(vendor missing)"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                rows {g.rowNumbers.join(", ")} · {g.items.length} item{g.items.length === 1 ? "" : "s"}
                                                {g.orderNumber ? ` · order ${g.orderNumber}` : ""}
                                                {g.carrier ? ` · ${g.carrier}` : ""}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                ${Number(g.invoiceTotal || 0).toFixed(2)}
                                            </Typography>
                                            {g.errors.length === 0 && <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />}
                                        </Box>
                                    </Box>

                                    {g.errors.length > 0 && (
                                        <Box sx={{ px: 1.5, py: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {g.errors.map((e, i) => (
                                                <Chip key={i} label={e} size="small" color="error" variant="outlined" sx={{ height: "auto", py: 0.4, fontSize: 10.5, "& .MuiChip-label": { whiteSpace: "normal" } }} />
                                            ))}
                                        </Box>
                                    )}

                                    <Box sx={{ overflowX: "auto" }}>
                                        <Table size="small" sx={{ minWidth: 520 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Row</TableCell>
                                                    <TableCell>Item</TableCell>
                                                    <TableCell align="right">Qty</TableCell>
                                                    <TableCell align="right">Unit Value</TableCell>
                                                    <TableCell>Tracking #</TableCell>
                                                    <TableCell>Issues</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {g.items.map((item) => (
                                                    <TableRow key={item.rowNumber} sx={{ bgcolor: item.errors.length ? "rgba(197,48,48,0.04)" : undefined }}>
                                                        <TableCell sx={{ fontFamily: "monospace" }}>{item.rowNumber}</TableCell>
                                                        <TableCell>{item.description || "—"}</TableCell>
                                                        <TableCell align="right">{item.quantity}</TableCell>
                                                        <TableCell align="right">${Number(item.unitValue || 0).toFixed(2)}</TableCell>
                                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{item.trackingNumber || "—"}</TableCell>
                                                        <TableCell>
                                                            {item.errors.length > 0 && (
                                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                                    {item.errors.map((e, i) => (
                                                                        <Chip key={i} label={e} size="small" color="error" variant="outlined" sx={{ height: "auto", py: 0.4, fontSize: 10.5, "& .MuiChip-label": { whiteSpace: "normal" } }} />
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Button onClick={() => setStep(STEPS.UPLOAD)} variant="outlined">
                                Back
                            </Button>
                            <Button
                                onClick={() => setStep(STEPS.INVOICES)}
                                variant="contained"
                                disabled={!validGroups.length || (showTinPicker && !tin)}
                            >
                                Next — Upload Invoices
                            </Button>
                        </Box>
                    </Box>
                )}

                {step === STEPS.INVOICES && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            Attach a Purchase Invoice for each of the {validGroups.length} shipment
                            {validGroups.length === 1 ? "" : "s"} below. Shipments aren't created until every one has
                            an invoice attached.
                        </Alert>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {validGroups.map((g, gi) => {
                                const key = groupKey(g);
                                const data = invoiceData[key] || { files: [], docType: "invoice" };
                                return (
                                    <Box key={key} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                {g.vendorName} — Order# {g.orderNumber}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                                ${Number(g.invoiceTotal || 0).toFixed(2)}
                                            </Typography>
                                        </Box>

                                        <TextField
                                            select
                                            label="Doc Type"
                                            value={data.docType}
                                            onChange={(e) => updateInvoiceData(key, { docType: e.target.value })}
                                            required
                                            size="small"
                                            sx={{ mb: 1.5, minWidth: 220 }}
                                        >
                                            <MenuItem value="invoice">Purchase Invoice</MenuItem>
                                            <MenuItem value="other">Other</MenuItem>
                                        </TextField>

                                        <GroupInvoiceUpload
                                            files={data.files}
                                            onChange={(files) => updateInvoiceData(key, { files })}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Button onClick={() => setStep(STEPS.REVIEW)} variant="outlined">
                                Back
                            </Button>
                            <Button onClick={handleConfirm} variant="contained" disabled={!allInvoicesAttached}>
                                {`Create ${validGroups.length} Shipment${validGroups.length === 1 ? "" : "s"}`}
                            </Button>
                        </Box>
                    </Box>
                )}

                {step === STEPS.IMPORTING && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 4 }}>
                        <LinearProgress
                            variant={importProgress.totalShipments ? "determinate" : "indeterminate"}
                            value={
                                importProgress.totalShipments
                                    ? Math.round((importProgress.shipmentsDone / importProgress.totalShipments) * 100)
                                    : 0
                            }
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body1" fontWeight={600} textAlign="center">
                            {importProgress.shipmentsDone} ({importProgress.itemsDone} items) of{" "}
                            {importProgress.totalShipments} ({importProgress.totalItems} items) shipments uploaded
                        </Typography>
                        <Typography variant="caption" color="text.secondary" textAlign="center">
                            Please keep this page open until the import finishes.
                        </Typography>
                    </Box>
                )}

                {step === STEPS.RESULT && result && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Alert
                            severity={result.createdCount > 0 ? "success" : "warning"}
                            sx={{ borderRadius: 2 }}
                            action={
                                <Button color="inherit" size="small" startIcon={<Download />} onClick={() => downloadReportCsv(result)}>
                                    Download Report
                                </Button>
                            }
                        >
                            {result.createdCount} shipment(s) created
                            {result.failedCount ? `, ${result.failedCount} shipment(s) failed` : ""}.
                        </Alert>

                        {result.created.length > 0 && (
                            <Box>
                                <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                                    Created
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    {result.created.map((c) => (
                                        <Chip
                                            key={c.shipmentId}
                                            label={`${c.trackingNumber} (${c.itemCount} item${c.itemCount === 1 ? "" : "s"})`}
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            sx={{ fontFamily: "monospace" }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {result.failed.length > 0 && (
                            <>
                                <Divider />
                                <Box>
                                    <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                                        Failed
                                    </Typography>
                                    {result.failed.map((f, i) => (
                                        <Typography key={i} variant="body2" color="error.main">
                                            Row(s) {f.rowNumbers.join(", ")}: {f.errors.join(", ")}
                                        </Typography>
                                    ))}
                                </Box>
                            </>
                        )}

                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button variant="contained" onClick={() => navigate("/dashboard/shipments")}>
                                Done — View Shipments
                            </Button>
                        </Box>
                    </Box>
                )}
            </Card>
        </Box>
    );
};

export default CustomerBulkImportShipments;
